import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Category } from '../../types';

interface NestedSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Category[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}

const NestedSelect: React.FC<NestedSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'انتخاب کنید',
  error,
  helperText,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Category[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get selected category title
  const selectedCategory = options.find(option => option.id === value);
  const selectedTitle = selectedCategory ? selectedCategory.title : '';

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter(option => 
      option.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.title_en.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build nested structure
  const buildNestedOptions = (categories: Category[], parentId: string = '', level: number = 0): Category[] => {
    const nested: Category[] = [];
    
    categories.forEach(category => {
      if (category.parent_id === parentId) {
        nested.push(category);
        const children = buildNestedOptions(categories, category.id, level + 1);
        nested.push(...children);
      }
    });
    
    return nested;
  };

  const nestedOptions = buildNestedOptions(filteredOptions);

  const getOptionIndent = (category: Category): number => {
    let level = 0;
    let current = category;
    
    while (current.parent_id) {
      const parent = options.find(opt => opt.id === current.parent_id);
      if (parent) {
        level++;
        current = parent;
      } else {
        break;
      }
    }
    
    return level;
  };

  const handleSelect = (categoryId: string) => {
    onChange(categoryId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-3 py-2 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
            error 
              ? 'border-red-500 focus:ring-red-500' 
              : 'border-gray-300 focus:ring-primary-500'
          } ${
            disabled 
              ? 'bg-gray-100 cursor-not-allowed' 
              : 'bg-white hover:border-gray-400 cursor-pointer'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`${selectedTitle ? 'text-gray-900' : 'text-gray-500'}`}>
              {selectedTitle || placeholder}
            </span>
            <div className="flex items-center space-x-2 space-x-reverse">
              {value && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="پاک کردن"
                >
                  <X className="h-3 w-3 text-gray-400" />
                </button>
              )}
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="جستجو در دسته‌بندی‌ها..."
                  className="w-full pr-3 pl-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {nestedOptions.length > 0 ? (
                nestedOptions.map((option) => {
                  const indent = getOptionIndent(option);
                  const isSelected = option.id === value;
                  
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelect(option.id)}
                      className={`w-full text-right px-3 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${
                        isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                      }`}
                      style={{ paddingRight: `${12 + (indent * 16)}px` }}
                    >
                      <div className="flex items-center">
                        {indent > 0 && (
                          <div className="flex items-center space-x-1 space-x-reverse mr-2">
                            {Array.from({ length: indent }).map((_, index) => (
                              <div key={index} className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            ))}
                          </div>
                        )}
                        <span className="truncate">{option.title}</span>
                        {option.title_en && (
                          <span className="text-gray-500 text-xs mr-2">({option.title_en})</span>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center text-gray-500 text-sm">
                  {searchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ دسته‌بندی‌ای موجود نیست'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default NestedSelect; 